import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { User, Shield, Edit2, Save, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const UsersPage = () => {
  const { t } = useTranslation('platform');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", company: "" });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = (user) => {
    setEditingUser(user.user_id);
    setEditForm({ role: user.role, company: user.company || "" });
  };

  const handleSave = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}`, editForm, { withCredentials: true });
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: "badge-cyan",
      auditor: "badge-blue",
      client: "badge-green"
    };
    return badges[role] || "badge-gray";
  };

  return (
    <QMSLayout title={t('platform:users.title')}>
      {/* Header */}
      <div className="mb-6">
        <p className="text-[#8B95A5]">{t('users.manageUsers')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="user-stats">
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('users.totalUsers')}</p>
          <p className="kpi-value">{users.length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#38BDF8" }}>
          <p className="kpi-label">{t('users.admins')}</p>
          <p className="kpi-value">{users.filter(u => u.role === "admin").length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('users.auditors')}</p>
          <p className="kpi-value">{users.filter(u => u.role === "auditor").length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
          <p className="kpi-label">{t('users.clients')}</p>
          <p className="kpi-value">{users.filter(u => u.role === "client").length}</p>
        </div>
      </div>

      {/* Users Table */}
      <div className="qms-card overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="qms-table">
            <thead>
              <tr>
                <th>{t('users.user')}</th>
                <th>{t('users.email')}</th>
                <th>{t('users.role')}</th>
                <th>{t('users.company')}</th>
                <th>{t('users.created')}</th>
                <th>{t('users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#8B95A5]">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#8B95A5]">No users found</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#2D313D] flex items-center justify-center">
                            <User size={16} className="text-[#8B95A5]" />
                          </div>
                        )}
                        <span className="text-white">{user.name}</span>
                      </div>
                    </td>
                    <td className="text-[#8B95A5]">{user.email}</td>
                    <td>
                      {editingUser === user.user_id ? (
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className="qms-select w-auto py-1"
                        >
                          <option value="admin">Admin</option>
                          <option value="auditor">Auditor</option>
                          <option value="client">Client</option>
                        </select>
                      ) : (
                        <span className={`badge ${getRoleBadge(user.role)} capitalize`}>
                          {user.role}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingUser === user.user_id ? (
                        <input
                          type="text"
                          value={editForm.company}
                          onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                          className="qms-input py-1 w-32"
                          placeholder={t('users.companyName')}
                        />
                      ) : (
                        <span className="text-[#8B95A5]">{user.company || "-"}</span>
                      )}
                    </td>
                    <td className="text-[#8B95A5] text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      {editingUser === user.user_id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSave(user.user_id)}
                            className="p-1 text-[#4ADE80] hover:text-white transition-colors"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="p-1 text-[#F87171] hover:text-white transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-1 text-[#8B95A5] hover:text-[#38BDF8] transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Permissions Info */}
      <div className="mt-8 grid md:grid-cols-3 gap-6" data-testid="role-info">
        <div className="qms-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-[3px] bg-[rgba(0,200,224,0.2)] flex items-center justify-center">
              <Shield size={20} className="text-[#38BDF8]" />
            </div>
            <h3 className="font-['Syne'] font-semibold text-white">{t('users.adminDesc.title')}</h3>
          </div>
          <ul className="text-[#8B95A5] text-sm space-y-2">
            <li>• {t('users.adminDesc.fullAccess')}</li>
            <li>• {t('users.adminDesc.userMgmt')}</li>
            <li>• {t('users.adminDesc.allCrud')}</li>
            <li>• {t('users.adminDesc.projectAssign')}</li>
            <li>• {t('users.adminDesc.reportGen')}</li>
          </ul>
        </div>

        <div className="qms-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-[3px] bg-[rgba(26,107,204,0.2)] flex items-center justify-center">
              <Shield size={20} className="text-[#36A9E1]" />
            </div>
            <h3 className="font-['Syne'] font-semibold text-white">{t('users.auditorDesc.title')}</h3>
          </div>
          <ul className="text-[#8B95A5] text-sm space-y-2">
            <li>• {t('users.auditorDesc.viewProjects')}</li>
            <li>• {t('users.auditorDesc.createCapas')}</li>
            <li>• {t('users.auditorDesc.createAudits')}</li>
            <li>• {t('users.auditorDesc.logDeviations')}</li>
            <li>• {t('users.auditorDesc.manageSuppliers')}</li>
          </ul>
        </div>

        <div className="qms-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-[3px] bg-[rgba(62,207,142,0.2)] flex items-center justify-center">
              <Shield size={20} className="text-[#4ADE80]" />
            </div>
            <h3 className="font-['Syne'] font-semibold text-white">{t('users.clientDesc.title')}</h3>
          </div>
          <ul className="text-[#8B95A5] text-sm space-y-2">
            <li>• {t('users.clientDesc.viewDashboard')}</li>
            <li>• {t('users.clientDesc.viewCapas')}</li>
            <li>• {t('users.clientDesc.viewAudits')}</li>
            <li>• {t('users.clientDesc.downloadDocs')}</li>
            <li>• {t('users.clientDesc.readOnly')}</li>
          </ul>
        </div>
      </div>
    </QMSLayout>
  );
};

export default UsersPage;
